/**
 * Bombs: what leaves a bomber's rack, where it lands, and what that does to the
 * ground.
 *
 * Written to the same contract `rounds.ts` holds for rifle rounds, because the
 * reasons are the same. A `BombRecord` is **a bomb in flight**, not a decoration
 * of one: it carries the point and velocity it was released at and the tick that
 * happened, and where it *is* falls out of those plus gravity
 * (`bombPositionAt`). The records live in the encounter state and therefore in
 * its hash, so a replay drops exactly the bombs the original run dropped, and
 * what a renderer draws is the object that did the damage.
 *
 * **Nothing here is random.** The trajectory is a parabola with no drag: a bomb
 * falls, and the only thing that bends it is gravity. The one place a real
 * bombsight would have error is the release decision, and that error is
 * expressed as `bombAimToleranceM`: the bomber solves the fall properly and lets
 * go when its *predicted impact point* is inside that circle, which makes a bad
 * release a matter of the aeroplane being off its run rather than of a die.
 *
 * **It falls through moving air** (AD-007k §9). The parabola is drawn in the air
 * mass, and the air mass is going somewhere: the same world-frame wind `step()`
 * adds to an aeroplane's flight path is added to a bomb's, at the band its
 * release altitude sits in, for the whole fall. That is not a drag model — it is
 * the frame the whole ballistic model already lives in, and leaving it out is
 * what makes a *crabbing* bomber miss. A bomber holding its track in a
 * crosswind points its nose upwind by the drift angle, so a bomb that kept only
 * the aeroplane's air-relative velocity would fly off upwind at exactly the
 * wind speed and land `wind × fall time` short of the track — 52 m in a 6 m/s
 * crosswind, which is past `bombAimToleranceM` and would stop a wartime raid
 * releasing at all. With the air's own motion in it, the predicted impact runs
 * down the *ground track* and the crab and the fall agree.
 *
 * The prediction and the fall use one expression, so whatever the wind does to
 * where a bomb lands it does to where the bomb-aimer thought it would land:
 * `predictedImpact` is exactly `bombImpactPoint` of the record the same release
 * would produce, wind and all.
 *
 * **The release altitude is the original's.** The Mission Builder manual states
 * it outright — *"To be sure of getting a hit, bombers will make their attack
 * run at 1200 feet"* — and that is `bombRunAltitudeM`, the one sourced tunable
 * in `bomb-tunables.ts`.
 */

import { GRAVITY_MPS2 } from "../flight/units.ts";
import type { Vec3 } from "../flight/math3d.ts";
import type { Tunables } from "../flight/tunables.ts";
import { ZERO_WIND, windVelocity, type WeatherKind } from "../flight/wind.ts";
import type { CombatBody } from "./geometry.ts";
import type { EncounterTarget } from "./targets.ts";
import { targetProfileOf } from "./target-profiles.ts";

/**
 * The air a raid's bombs fall through: the encounter's weather and its REALISTIC
 * WEATHER gate, which is everything `wind.ts` needs besides an altitude. Every
 * aeroplane in an encounter is stamped with the same pair (`mission-spec.ts`),
 * so this is one record for the whole roster rather than one per rack.
 */
export interface BombAir {
  /** The resolved REALISTIC WEATHER subsystem flag. Off means no wind at all. */
  readonly enabled: boolean;
  readonly weather: WeatherKind;
  readonly tunables: Tunables;
}

/**
 * The wind a bomb released at `releaseAltitudeM` falls in, held for the whole
 * fall.
 *
 * Fixed at the release band rather than re-read as the bomb descends, and that
 * is what keeps the trajectory a closed form: a bomb's position stays a function
 * of its own record and the clock, which is what the replay, the collision
 * sweep and the renderer all need. The approximation it costs is small and
 * one-sided — only the last stretch below 500 ft is in a different band
 * (`WIND_BAND_PROFILE`'s half-strength band 0), which for a release at the
 * manual's 1,200 ft is the final two seconds of a nine-second fall.
 */
export function bombWind(air: BombAir | undefined, releaseAltitudeM: number): Vec3 {
  if (!air) return ZERO_WIND;
  return windVelocity({
    altitudeM: releaseAltitudeM,
    grounded: false,
    enabled: air.enabled,
    weather: air.weather,
    tunables: air.tunables,
  });
}

/**
 * `point` carried `seconds` downwind. Returns the point **itself** when there is
 * no wind, so every bomb in a still-air encounter follows exactly the parabola
 * it followed before wind existed — the identity the seed-pinned raid fixtures
 * rest on.
 */
function advected(point: Vec3, wind: Vec3, seconds: number): Vec3 {
  if (wind.x === 0 && wind.y === 0 && wind.z === 0) return point;
  return {
    x: point.x + wind.x * seconds,
    y: point.y + wind.y * seconds,
    z: point.z + wind.z * seconds,
  };
}

export interface BombRecord {
  readonly bomberId: string;
  /** This bomb's place in its bomber's load, so a stick is ordered and a renderer can tell two apart. */
  readonly bombIndex: number;
  /** World position at the instant of release. */
  readonly from: Vec3;
  /** World velocity at release — the aeroplane's, plus whatever the rack adds. */
  readonly velocity: Vec3;
  readonly releaseTick: number;
  /** The height it detonates at: the ground under the aiming point. */
  readonly impactY: number;
}

export interface BombImpact {
  readonly bomberId: string;
  readonly bombIndex: number;
  readonly position: Vec3;
}

/**
 * Hard ceiling on bombs in the air, for the same reason `MAX_LIVE_ROUNDS`
 * exists: the hashable state has to stay bounded. A raid of six heavies with
 * twelve bombs each is 72, and each is aloft for about nine seconds, so this is
 * well clear of anything a mission can produce.
 */
export const MAX_LIVE_BOMBS = 128;

/** Seconds to fall `heightM` from a standing start. The half of the solution that has no horizontal part in it. */
export function freeFallSeconds(heightM: number): number {
  return Math.sqrt(Math.max(0, 2 * heightM) / GRAVITY_MPS2);
}

/**
 * Seconds until a body released at `body` reaches `impactY`. Solves the whole
 * quadratic rather than assuming a level release, so a bomber in a shallow dive
 * or a climb gets the right answer.
 */
export function fallSeconds(body: CombatBody, impactY: number): number {
  const drop = body.position.y - impactY;
  if (drop <= 0) return 0;
  const vy = body.velocity.y;
  return (vy + Math.sqrt(vy * vy + 2 * GRAVITY_MPS2 * drop)) / GRAVITY_MPS2;
}

/**
 * Where a bomb released from `body` right now would land, in the air `wind`
 * describes. The wind term is the same one the released bomb will fly with, so
 * this is a prediction and not an estimate.
 */
export function predictedImpact(
  body: CombatBody,
  impactY: number,
  tunables: Tunables,
  wind: Vec3 = ZERO_WIND,
): Vec3 {
  const release: CombatBody = {
    ...body,
    velocity: { ...body.velocity, y: body.velocity.y - tunables.bombEjectionSpeedMps },
  };
  const t = fallSeconds(release, impactY);
  const still: Vec3 = {
    x: release.position.x + release.velocity.x * t,
    y: impactY,
    z: release.position.z + release.velocity.z * t,
  };
  return { ...advected(still, wind, t), y: impactY };
}

export interface ReleaseView {
  readonly body: CombatBody;
  /** The point on the ground the bomber is trying to hit. */
  readonly aimPoint: Vec3;
  readonly bombsRemaining: number;
  /** The tick the last bomb of this stick left, or `null` if none has. */
  readonly lastReleaseTick: number | null;
  readonly tick: number;
  readonly dt: number;
  readonly tunables: Tunables;
  /**
   * The air this rack's bombs will fall through. Omitted by a caller with no
   * weather to declare — a bare fixture, or the flight core's own tests — which
   * is still air and the behaviour every one of them had before wind existed.
   */
  readonly air?: BombAir;
}

/** Horizontal miss distance between where this bomb would land and where it should. */
export function aimErrorM(view: ReleaseView): number {
  const impact = predictedImpact(view.body, view.aimPoint.y, view.tunables, releaseWind(view));
  return Math.hypot(impact.x - view.aimPoint.x, impact.z - view.aimPoint.z);
}

/** The wind at this rack's own height — where its next bomb would leave it. */
function releaseWind(view: ReleaseView): Vec3 {
  return bombWind(view.air, view.body.position.y);
}

/**
 * The three conditions a release has regardless of *who* decides it: there is
 * something left to drop, there is height to drop it through, and the stick's
 * own interval has elapsed since the last one.
 *
 * Split out because the player's rack works the same way an AI bomber's does in
 * every respect but the decision — he pulls the toggle instead of solving the
 * fall — and duplicating the three gates on the commanded path is how the two
 * cadences would drift apart.
 */
export function rackReady(view: ReleaseView): boolean {
  if (view.bombsRemaining <= 0) return false;
  if (view.body.position.y <= view.aimPoint.y) return false;
  if (view.lastReleaseTick === null) return true;
  return (view.tick - view.lastReleaseTick) * view.dt >= view.tunables.bombReleaseIntervalSeconds;
}

/**
 * Whether a bomb leaves an *aimed* rack this tick — a bomber's or a Zeppelin's.
 * The three shared gates and no die, plus the one this path adds: the solution
 * is good.
 */
export function shouldRelease(view: ReleaseView): boolean {
  if (!rackReady(view)) return false;
  return aimErrorM(view) <= view.tunables.bombAimToleranceM;
}

export function releaseBomb(view: ReleaseView, bomberId: string, bombIndex: number): BombRecord {
  return {
    bomberId,
    bombIndex,
    from: { ...view.body.position },
    velocity: {
      x: view.body.velocity.x,
      y: view.body.velocity.y - view.tunables.bombEjectionSpeedMps,
      z: view.body.velocity.z,
    },
    releaseTick: view.tick,
    impactY: view.aimPoint.y,
  };
}

/** Seconds this bomb has been falling at `tick`. Clamped at 0 so a record read on its own release tick sits on the rack. */
export function bombFlightSeconds(record: BombRecord, tick: number, dt: number): number {
  return Math.max(0, tick - record.releaseTick) * dt;
}

/** Where the bomb is at `tick`: release point, plus its release velocity, plus gravity, plus the air's own motion. */
export function bombPositionAt(record: BombRecord, tick: number, dt: number, wind: Vec3 = ZERO_WIND): Vec3 {
  const t = bombFlightSeconds(record, tick, dt);
  return advected(
    {
      x: record.from.x + record.velocity.x * t,
      y: record.from.y + record.velocity.y * t - 0.5 * GRAVITY_MPS2 * t * t,
      z: record.from.z + record.velocity.z * t,
    },
    wind,
    t,
  );
}

/**
 * The exact point this bomb reaches `impactY` at — solved, not sampled. Taking
 * the position on the first tick past the ground would move the crater by up to
 * a tick of travel (≈2 m at release speed), which is enough to change whether a
 * near miss counted.
 */
export function bombImpactPoint(record: BombRecord, wind: Vec3 = ZERO_WIND): Vec3 {
  const t = fallSeconds({ position: record.from, velocity: record.velocity, orientation: { x: 0, y: 0, z: 0, w: 1 } }, record.impactY);
  const still: Vec3 = {
    x: record.from.x + record.velocity.x * t,
    y: record.impactY,
    z: record.from.z + record.velocity.z * t,
  };
  return { ...advected(still, wind, t), y: record.impactY };
}

export interface BombSweep {
  readonly bombs: readonly BombRecord[];
  readonly impacts: readonly BombImpact[];
}

/**
 * Advances every bomb one tick: the ones still above their impact height fly on,
 * the rest go off. Each takes the wind of the band it was released in, read off
 * its own record, so a stick dropped high and a Zeppelin's load dropped higher
 * still drift by their own amounts within the one sweep.
 */
export function advanceBombs(
  bombs: readonly BombRecord[],
  tick: number,
  dt: number,
  air?: BombAir,
): BombSweep {
  const flying: BombRecord[] = [];
  const impacts: BombImpact[] = [];
  for (const record of bombs) {
    const wind = bombWind(air, record.from.y);
    if (bombPositionAt(record, tick, dt, wind).y > record.impactY) {
      flying.push(record);
      continue;
    }
    impacts.push({ bomberId: record.bomberId, bombIndex: record.bombIndex, position: bombImpactPoint(record, wind) });
  }
  return { bombs: flying.length <= MAX_LIVE_BOMBS ? flying : flying.slice(flying.length - MAX_LIVE_BOMBS), impacts };
}

/**
 * The fraction of a bomb's integrity a target standing at `target` takes from a
 * burst at `impact`: full inside its own footprint, falling linearly to nothing
 * at `bombBlastRadiusM` past it, and zero for anything the blast cannot touch.
 *
 * Measured horizontally on purpose. A bomb goes off on the deck, so what decides
 * whether a shed is inside the blast is how far away it is across the ground,
 * not how tall it happens to be.
 */
export function blastShare(impact: Vec3, target: EncounterTarget, tunables: Tunables): number {
  const kindProfile = targetProfileOf(target.kind);
  if (kindProfile.bombVulnerability <= 0) return 0;
  const distance = Math.hypot(impact.x - target.position.x, impact.z - target.position.z);
  const beyond = distance - kindProfile.footprintRadiusM;
  if (beyond <= 0) return tunables.bombDamageAtCentre * kindProfile.bombVulnerability;
  const radius = Math.max(tunables.bombBlastRadiusM, 1);
  if (beyond >= radius) return 0;
  return tunables.bombDamageAtCentre * kindProfile.bombVulnerability * (1 - beyond / radius);
}
